import { RadioGroup, RadioGroupItem } from '@eigtech/ui/components/radio-group'
import { FieldPath, FieldPathValue, FieldValues, useController } from 'react-hook-form'
import type { RequireAtLeastOne } from 'type-fest'
import { WithFieldNameProp } from '../types/FieldProps'
import { BaseProps, FieldControl } from './FieldControl'

type Props<TFieldValues extends FieldValues = FieldValues> = {
  radioGroupProps?: React.ComponentProps<typeof RadioGroup>
  stackProps?: React.ComponentProps<'div'>
} & BaseProps<TFieldValues>

export type RadioGroupFieldProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  WithFieldNameProp<Props<TFieldValues>, TFieldValues>,
  'children'
> &
  RequireAtLeastOne<
    {
      defaultValue?: FieldPathValue<TFieldValues, FieldPath<TFieldValues>>
      children?: React.ReactNode
      options?: { value: string; label?: string }[]
      radioProps?: React.ComponentProps<typeof RadioGroupItem>
    },
    'children' | 'options'
  >

export function RadioGroupField<TFieldValues extends FieldValues = FieldValues>({
  children,
  control,
  defaultValue,
  label,
  name,
  options,
  radioGroupProps,
  radioProps,
  stackProps,
  ...props
}: RadioGroupFieldProps<TFieldValues>) {
  const {
    field,
    formState: { isSubmitting },
  } = useController({
    name,
    control,
    defaultValue,
  })

  return (
    <FieldControl control={control} disabled={isSubmitting} label={label} name={name} {...props}>
      <RadioGroup {...field} {...radioGroupProps}>
        <div className="flex flex-row gap-2" {...stackProps}>
          {children ??
            (options ?? []).map(({ value, label }) => (
              <label key={value} className="flex items-center gap-2">
                <RadioGroupItem
                  disabled={isSubmitting || props.disabled}
                  {...radioProps}
                  value={value}
                />

                {label ?? value}
              </label>
            ))}
        </div>
      </RadioGroup>
    </FieldControl>
  )
}

export type RadioGroupFieldComponent<TFieldValues extends FieldValues = FieldValues> =
  typeof RadioGroupField<TFieldValues>
